import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

/**
 * Workshop: Space switcher — compact dot-based switcher for workspace spaces.
 */

interface Space {
  id: string;
  label: string;
  active?: boolean;
}

interface SpaceSwitcherProps {
  spaces: Space[];
  onSelect: (spaceId: string) => void;
  onAddSpace?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function SpaceSwitcher({
  spaces,
  onSelect,
  onAddSpace,
  className,
  "data-testid": testId = "space-switcher",
}: SpaceSwitcherProps) {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-full border border-glass-stroke bg-glass-fill px-3 py-1.5 shadow-glass backdrop-blur-glass-sm",
        className,
      )}
      data-testid={testId}
      role="tablist"
    >
      {spaces.map((space) => (
        <button
          key={space.id}
          role="tab"
          aria-selected={space.active}
          aria-label={space.label}
          onClick={() => onSelect(space.id)}
          className="group relative flex items-center justify-center"
          data-testid={`space-tab-${space.id}`}
        >
          <span
            className={cn(
              "block rounded-full transition-all",
              space.active
                ? "h-2.5 w-2.5 bg-accent shadow-glow-accent-sm"
                : "h-2 w-2 bg-muted hover:bg-secondary",
            )}
          />
          <span className="pointer-events-none absolute -bottom-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-surface px-2 py-0.5 text-canvas-base font-medium text-primary opacity-0 shadow-glass transition-opacity group-hover:opacity-100">
            {space.label}
          </span>
        </button>
      ))}

      <button
        type="button"
        onClick={onAddSpace}
        className="flex h-4 w-4 items-center justify-center rounded-full text-muted transition-colors hover:text-primary"
        aria-label="Add space"
        data-testid="space-switcher-add"
      >
        <Icons.Plus className="h-3 w-3" />
      </button>
    </div>
  );
}

export type { SpaceSwitcherProps, Space };
